import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, MessageCircle, PhoneCall } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../api";

export default function InteracoesModal({
  isOpen,
  onClose,
  lead,
  tarefas = [],
  onChangeInteracoes,
}) {
  const agora = new Date();

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [novaInteracao, setNovaInteracao] = useState({
    tipo: "whatsapp",
    descricao: "",
    data: agora.toISOString().split("T")[0],
    hora: agora.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  });
  const [salvando, setSalvando] = useState(false);
  const [listaInteracoes, setListaInteracoes] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const textareaRef = useRef(null);

  function getEmpresaId() {
    try {
      const empresaAtiva = JSON.parse(localStorage.getItem("empresaAtiva"));
      const usuarioLogado = JSON.parse(localStorage.getItem("usuarioLogado"));
      return empresaAtiva?.id || usuarioLogado?.empresa_id || "";
    } catch {
      return "";
    }
  }

  function getEstadoInicialInteracao() {
    const agoraLocal = new Date();
    return {
      tipo: "whatsapp",
      descricao: "",
      data: agoraLocal.toISOString().split("T")[0],
      hora: agoraLocal.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    };
  }

  function limparTexto(texto) {
    return String(texto || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function contarPalavras(texto) {
    return limparTexto(texto).split(" ").filter(Boolean).length;
  }

  function validarDescricaoInteracao(descricao) {
    const textoLimpo = limparTexto(descricao);
    const quantidadePalavras = contarPalavras(textoLimpo);

    if (!textoLimpo) {
      return {
        ok: false,
        mensagem: "Preencha a descrição da interação.",
      };
    }

    if (textoLimpo.length < 30) {
      return {
        ok: false,
        mensagem: "A descrição da interação deve ter no mínimo 30 caracteres.",
      };
    }

    if (quantidadePalavras < 5) {
      return {
        ok: false,
        mensagem: "A descrição da interação deve ter no mínimo 5 palavras.",
      };
    }

    return {
      ok: true,
      textoLimpo,
      quantidadePalavras,
    };
  }

  function validarDataHoraInteracao(data, hora) {
    if (!data) {
      return { ok: false, mensagem: "Informe a data da interação." };
    }

    if (!hora) {
      return { ok: false, mensagem: "Informe a hora da interação." };
    }

    const dataHora = new Date(
      `${data}T${hora.length === 5 ? `${hora}:00` : hora}`,
    );
    if (isNaN(dataHora.getTime())) {
      return { ok: false, mensagem: "Data ou hora da interação inválida." };
    }

    const agoraLocal = new Date();
    if (dataHora > agoraLocal) {
      return {
        ok: false,
        mensagem: "A interação não pode ser registrada com data/hora futura.",
      };
    }

    return { ok: true };
  }

  const buscarInteracoes = async () => {
    try {
      const empresaId = String(getEmpresaId()).trim();

      if (!empresaId || !lead?.id) {
        setListaInteracoes([]);
        console.warn(
          "⚠️ empresaId ou lead.id ausente, abortando fetch de interações.",
        );
        return [];
      }

      const params = { empresa_id: empresaId, id_lead: lead.id };
      const res = await api.get("/interacoes", { params });

      const interacoes = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];

      setListaInteracoes(interacoes);

      console.log(
        "🟢 Interações carregadas:",
        interacoes,
        "Empresa:",
        empresaId,
        "Lead:",
        lead?.id,
      );

      return interacoes;
    } catch (e) {
      console.error("❌ Erro ao buscar interações:", e);
      setListaInteracoes([]);
      return [];
    }
  };

  useEffect(() => {
    if (isOpen && lead?.id) {
      buscarInteracoes();
    }
  }, [isOpen, lead]);

  useEffect(() => {
    if (mostrarFormulario && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [mostrarFormulario]);

  useEffect(() => {
    if (isOpen) {
      setNovaInteracao(getEstadoInicialInteracao());
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNovaInteracao((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelar = () => {
    setNovaInteracao(getEstadoInicialInteracao());
    setMostrarFormulario(false);
  };

  const handleAdicionar = async () => {
    if (!lead?.id) {
      toast.error("Lead não identificada para registrar interação.");
      return;
    }

    const tarefasDaLead = tarefas.filter(
      (t) => String(t.lead_id) === String(lead.id),
    );

    const temTarefaAtiva = tarefasDaLead.some((t) => {
      const status = (t.status || "").trim().toLowerCase();
      return status === "pendente" || status === "em andamento";
    });

    if (!temTarefaAtiva) {
      toast.warn(
        "⚠️ Você pode registrar a interação agora. O ideal é criar uma tarefa em seguida para manter o acompanhamento desta lead.",
        { autoClose: 5000 },
      );
    }

    const validacaoDescricao = validarDescricaoInteracao(
      novaInteracao.descricao,
    );
    if (!validacaoDescricao.ok) {
      toast.error(validacaoDescricao.mensagem);
      return;
    }

    const validacaoDataHora = validarDataHoraInteracao(
      novaInteracao.data,
      novaInteracao.hora,
    );
    if (!validacaoDataHora.ok) {
      toast.error(validacaoDataHora.mensagem);
      return;
    }

    setSalvando(true);

    try {
      const empresaId = getEmpresaId();

      if (!empresaId) {
        throw new Error("Empresa ativa não definida!");
      }

      const payload = {
        ...novaInteracao,
        descricao: validacaoDescricao.textoLimpo,
        id_lead: lead.id,
        empresa_id: empresaId,
      };

      await api.post("/interacoes", payload);

      const interacoesAtualizadas = await buscarInteracoes();

      if (typeof onChangeInteracoes === "function") {
        onChangeInteracoes(interacoesAtualizadas);
      }

      toast.success("✅ Interação salva com sucesso!");
      setNovaInteracao(getEstadoInicialInteracao());
      setMostrarFormulario(false);
    } catch (err) {
      console.error("❌ Erro ao salvar interação:", err);

      const mensagemBackend =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao salvar interação.";

      toast.error(mensagemBackend);
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluirInteracao = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta interação?")) {
      return;
    }

    try {
      const empresaId = getEmpresaId();

      await api.delete(`/interacoes/${id}`, {
        params: { empresa_id: empresaId },
      });

      const interacoesAtualizadas = await buscarInteracoes();

      if (typeof onChangeInteracoes === "function") {
        onChangeInteracoes(interacoesAtualizadas);
      }

      toast.success("Interação excluída com sucesso!");
    } catch (err) {
      console.error("❌ Erro ao excluir interação:", err);

      const mensagemBackend =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Erro ao excluir interação.";

      toast.error(mensagemBackend);
    }
  };

  const descricaoLimpaAtual = limparTexto(novaInteracao.descricao);
  const totalCaracteres = descricaoLimpaAtual.length;
  const totalPalavras = contarPalavras(novaInteracao.descricao);
  const descricaoValidaPreview = totalCaracteres >= 30 && totalPalavras >= 5;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="absolute top-0 right-0 w-full sm:w-[500px] h-full bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white shadow-2xl p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 pb-4 border-b border-slate-600">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    Histórico de Ligações e Mensagens
                  </h2>
                  <p className="text-sm text-slate-400">
                    Veja todas as interações registradas com este paciente.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="hover:text-red-400 transition"
                  type="button"
                >
                  <X className="w-6 h-6 text-slate-300" />
                </button>
              </div>
            </div>

            <div className="flex justify-end mb-4">
              {!mostrarFormulario && (
                <button
                  onClick={() => setMostrarFormulario(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 rounded-lg text-white text-sm shadow-lg transition"
                  type="button"
                >
                  <Plus className="w-4 h-4" /> Nova Interação
                </button>
              )}
            </div>

            {mostrarFormulario && (
              <div className="mb-6 bg-slate-800/60 rounded-lg p-4 shadow-xl backdrop-blur-sm">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input
                    type="date"
                    name="data"
                    value={novaInteracao.data}
                    onChange={handleChange}
                    className="rounded-lg px-3 py-1 bg-white text-black border border-blue-300 text-sm"
                  />
                  <input
                    type="time"
                    name="hora"
                    value={novaInteracao.hora}
                    onChange={handleChange}
                    className="rounded-lg px-3 py-1 bg-white text-black border border-blue-300 text-sm"
                  />
                </div>

                <select
                  name="tipo"
                  value={novaInteracao.tipo}
                  onChange={handleChange}
                  className="rounded-lg px-3 py-2 bg-white text-black border border-blue-300 w-full text-sm mb-3"
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="ligacao">Ligação</option>
                  <option value="kommo">Kommo</option>
                </select>

                <textarea
                  ref={textareaRef}
                  name="descricao"
                  value={novaInteracao.descricao}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Descreva a interação com contexto suficiente para outro usuário entender o andamento da lead."
                  className="rounded-lg px-3 py-2 bg-white text-black border border-blue-300 w-full text-sm resize-none"
                />

                <div className="mt-2 flex items-center justify-between text-xs">
                  <span
                    className={
                      descricaoValidaPreview
                        ? "text-green-400"
                        : "text-yellow-300"
                    }
                  >
                    {totalCaracteres}/30 caracteres • {totalPalavras}/5 palavras
                  </span>

                  {!descricaoValidaPreview && (
                    <span className="text-slate-300">
                      Mínimo: 30 caracteres e 5 palavras
                    </span>
                  )}
                </div>

                <div className="flex gap-3 mt-3">
                  <button
                    onClick={handleAdicionar}
                    disabled={salvando}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 ${
                      salvando
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800"
                    } rounded-lg text-sm shadow-lg transition`}
                    type="button"
                  >
                    {salvando ? (
                      "Salvando..."
                    ) : (
                      <>
                        <Plus className="w-4 h-4" /> Salvar
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleCancelar}
                    disabled={salvando}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 rounded-lg text-sm shadow-lg transition"
                    type="button"
                  >
                    <X className="w-4 h-4" /> Cancelar
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end mb-4">
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                className="rounded-lg px-3 py-2 bg-slate-700 text-white border border-slate-500 text-sm"
              >
                <option value="todos">Todos</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="ligacao">Ligação</option>
                <option value="kommo">Kommo</option>
              </select>
            </div>

            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {(() => {
                const listaOk = Array.isArray(listaInteracoes)
                  ? listaInteracoes
                  : [];

                if (listaOk.length === 0) {
                  return (
                    <div className="text-center text-slate-400">
                      Sem interações cadastradas.
                    </div>
                  );
                }

                const listaFiltrada = listaOk.filter(
                  (item) =>
                    filtroTipo === "todos" ||
                    (item.tipo || "").toLowerCase().trim() ===
                      filtroTipo.toLowerCase(),
                );

                if (listaFiltrada.length === 0) {
                  return (
                    <div className="text-center text-slate-400">
                      Sem interações do tipo selecionado.
                    </div>
                  );
                }

                return listaFiltrada.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex gap-3 bg-slate-800/60 rounded-lg p-3 border border-slate-700 shadow-xl backdrop-blur-sm"
                  >
                    <div className="pt-1">
                      {item.tipo?.toLowerCase() === "whatsapp" ? (
                        <MessageCircle className="w-5 h-5 text-green-400" />
                      ) : item.tipo?.toLowerCase() === "ligacao" ? (
                        <PhoneCall className="w-5 h-5 text-blue-400" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold">
                          {(item.tipo?.[0] || "O").toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300 uppercase tracking-wide">
                          {item.tipo || "Outro"}
                        </span>
                      </div>

                      <p className="text-sm text-slate-200">{item.descricao}</p>

                      <p className="text-xs text-slate-400 mt-1">
                        {item.data
                          ? new Date(item.data).toLocaleDateString("pt-BR")
                          : "--/--/----"}
                        {" • "}
                        {item.hora ? item.hora.slice(0, 5) : "--:--"}
                      </p>
                    </div>

                    <button
                      onClick={() => handleExcluirInteracao(item.id)}
                      className="ml-auto text-red-400 hover:text-red-600 transition"
                      title="Excluir Interação"
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ));
              })()}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
